import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, DatePicker, Descriptions, Divider, InputNumber, message, Select, Space, Spin, Typography } from 'antd';
import type { ColumnType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import StandardTable from '../../../components/common/StandardTable';
import CombinedActivitySelectorModal from '../../../components/modals/CombinedActivitySelectorModal';
import { useAuth } from '../../../components/common/security/AuthContext';
import type { PrimaveraTaskSmall } from '../../../types';
import { useGetTkfTaskResources } from '../../../hooks/genericHooks';
import { P6_STATUS_CODES } from '../../../constants/p6';
import { computePhysCompletePctCostWeighted, saveP6ProgressUpdate } from '../../../components/common/P6ProgressUpdate';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

type TaskRsrcRow = {
  taskrsrc_id: number;
  task_id: number;
  proj_id: number;
  rsrc_id: number | null;
  unit_id: number | null;
  unit_name: string | null;
  act_start_date?: string | null;
  act_end_date?: string | null;
  rsrc_short_name: string | null;
  rsrc_name: string | null;
  rsrc_type: string;
  cost_per_qty: number | null;
  cost_per_qty_source_type: string | null;
  target_qty: number | null;
  act_reg_qty: number | null;
  remain_qty: number | null;
  update_date?: string | null;
  update_user?: string | null;
};

type EditableTaskRsrcRow = TaskRsrcRow & {
  _new_act_reg_qty: number | null;
  _new_remain_qty: number | null;
};

type TaskDetails = {
  task_id: number;
  status_code: string | null;
  act_start_date: string | null;
  act_end_date: string | null;
  target_start_date?: string | null;
  target_end_date?: string | null;
  complete_pct_type?: string | null;
  phys_complete_pct?: number | null;
  target_drtn_hr_cnt?: number | null;
  remain_drtn_hr_cnt?: number | null;
};

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const round6 = (v: number): number => {
  return Math.round(v * 1_000_000) / 1_000_000;
};

const clampNonNegative = (v: number): number => {
  return v < 0 ? 0 : v;
};

const formatDate = (v: any): string => {
  if (!v) return '-';
  const d = dayjs(v);
  return d.isValid() ? d.format('DD/MM/YYYY') : String(v);
};

const ToPlnSchProgressUpdate: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<PrimaveraTaskSmall | null>(null);

  const [rows, setRows] = useState<EditableTaskRsrcRow[]>([]);
  const [saving, setSaving] = useState(false);

  const [progressDate, setProgressDate] = useState<Dayjs>(() => dayjs());
  const [taskDetails, setTaskDetails] = useState<TaskDetails | null>(null);
  const [selectedStatusCode, setSelectedStatusCode] = useState<string | null>(null);

  const taskId = selectedActivity?.task_id != null ? Number(selectedActivity.task_id) : null;

  const { data: taskRsrcResp, isLoading: isLoadingTaskRsrc } = useGetTkfTaskResources(taskId);

  useEffect(() => {
    if (taskId == null) {
      setRows([]);
      setTaskDetails(null);
      setSelectedStatusCode(null);
      return;
    }

    const unwrap = (resp: any): TaskRsrcRow[] => {
      if (Array.isArray(resp)) return resp;
      if (Array.isArray(resp?.data)) return resp.data;
      if (Array.isArray(resp?.data?.data)) return resp.data.data;
      return [];
    };

    const data = unwrap(taskRsrcResp);

    const mapped: EditableTaskRsrcRow[] = (data || []).map((r) => ({
      ...r,
      taskrsrc_id: Number((r as any).taskrsrc_id),
      task_id: Number((r as any).task_id),
      proj_id: Number((r as any).proj_id),
      rsrc_id: (r as any).rsrc_id != null ? Number((r as any).rsrc_id) : null,
      unit_id: (r as any).unit_id != null ? Number((r as any).unit_id) : null,
      unit_name: (r as any).unit_name != null ? String((r as any).unit_name) : null,
      act_start_date: (r as any).act_start_date != null ? String((r as any).act_start_date) : null,
      act_end_date: (r as any).act_end_date != null ? String((r as any).act_end_date) : null,
      rsrc_short_name: (r as any).rsrc_short_name != null ? String((r as any).rsrc_short_name) : null,
      rsrc_name: (r as any).rsrc_name != null ? String((r as any).rsrc_name) : null,
      cost_per_qty: toNum((r as any).cost_per_qty),
      cost_per_qty_source_type: (r as any).cost_per_qty_source_type != null ? String((r as any).cost_per_qty_source_type) : null,
      target_qty: toNum((r as any).target_qty),
      act_reg_qty: toNum((r as any).act_reg_qty),
      remain_qty: toNum((r as any).remain_qty),
      _new_act_reg_qty: toNum((r as any).act_reg_qty),
      _new_remain_qty: toNum((r as any).remain_qty),
    }));

    setRows(mapped);
  }, [taskId, taskRsrcResp]);

  useEffect(() => {
    if (!selectedActivity?.task_id) {
      setTaskDetails(null);
      setSelectedStatusCode(null);
      return;
    }

    const details: TaskDetails = {
      task_id: Number(selectedActivity.task_id),
      status_code: selectedActivity.status_code != null ? String(selectedActivity.status_code) : null,
      act_start_date: (selectedActivity as any).act_start_date != null ? String((selectedActivity as any).act_start_date) : null,
      act_end_date: (selectedActivity as any).act_end_date != null ? String((selectedActivity as any).act_end_date) : null,
      target_start_date: (selectedActivity as any).target_start_date != null ? String((selectedActivity as any).target_start_date) : null,
      target_end_date: (selectedActivity as any).target_end_date != null ? String((selectedActivity as any).target_end_date) : null,
      complete_pct_type: selectedActivity.complete_pct_type != null ? String(selectedActivity.complete_pct_type) : null,
      phys_complete_pct: toNum(selectedActivity.phys_complete_pct),
      target_drtn_hr_cnt: toNum((selectedActivity as any).target_drtn_hr_cnt),
      remain_drtn_hr_cnt: toNum((selectedActivity as any).remain_drtn_hr_cnt),
    };
    setTaskDetails(details);
    setSelectedStatusCode(details.status_code);
  }, [selectedActivity]);

  const hasChanges = useMemo(() => {
    return rows.some((r) => {
      const oldVal = toNum(r.act_reg_qty);
      const newVal = toNum(r._new_act_reg_qty);
      const oldRemain = toNum(r.remain_qty);
      const newRemain = toNum(r._new_remain_qty);
      return oldVal !== newVal || oldRemain !== newRemain;
    });
  }, [rows]);

  const statusChanged = useMemo(() => {
    const nextStatus = selectedStatusCode != null ? String(selectedStatusCode) : null;
    const currentStatus = taskDetails?.status_code != null ? String(taskDetails.status_code) : null;
    return nextStatus != null && nextStatus !== currentStatus;
  }, [selectedStatusCode, taskDetails]);

  const hasProgressValues = useMemo(() => {
    const anyActualQty = (rows || []).some((r) => {
      const actual = toNum((r as any)?._new_act_reg_qty) ?? toNum((r as any)?.act_reg_qty) ?? 0;
      return actual > 0;
    });
    const hasActStart = Boolean(taskDetails?.act_start_date);
    const physPct = toNum(taskDetails?.phys_complete_pct) ?? 0;
    return anyActualQty || hasActStart || physPct > 0;
  }, [rows, taskDetails]);

  const statusOptions = useMemo(() => {
    const currentStatus = taskDetails?.status_code != null ? String(taskDetails.status_code) : null;
    const canCompleteFromCurrent = currentStatus === 'TK_Active' || currentStatus === 'TK_Complete';
    return Array.from(P6_STATUS_CODES).map((c) => ({
      value: c,
      label: c,
      disabled:
        c === 'TK_Aside'
          ? true
          : c === 'TK_NotStart' && hasProgressValues
            ? true
            : c === 'TK_Complete'
              ? !canCompleteFromCurrent
              : false,
    }));
  }, [hasProgressValues, taskDetails]);

  const isPhysPct = taskDetails?.complete_pct_type === 'CP_Phys';

  const computedPhysCompletePct = useMemo(() => {
    if (!isPhysPct) return null;
    const nextStatus = selectedStatusCode != null ? String(selectedStatusCode) : null;
    return computePhysCompletePctCostWeighted({ rows, nextStatus });
  }, [isPhysPct, rows, selectedStatusCode]);

  const computedTotalCost = useMemo(() => {
    return (rows || []).reduce((acc, r) => {
      const qty = toNum((r as any)?.target_qty) ?? 0;
      const rate = toNum((r as any)?.cost_per_qty) ?? 0;
      return acc + qty * rate;
    }, 0);
  }, [rows]);

  const computedActualCost = useMemo(() => {
    return (rows || []).reduce((acc, r) => {
      const actual = toNum((r as any)?._new_act_reg_qty) ?? toNum((r as any)?.act_reg_qty) ?? 0;
      const target = toNum((r as any)?.target_qty) ?? 0;
      const qty = Math.min(actual, target);
      const rate = toNum((r as any)?.cost_per_qty) ?? 0;
      return acc + qty * rate;
    }, 0);
  }, [rows]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      if (taskId == null || !taskDetails) return;
      const progressIso = (progressDate ?? dayjs()).toDate().toISOString();
      const nextStatusCode = selectedStatusCode != null ? String(selectedStatusCode) : null;

      const result = await saveP6ProgressUpdate({
        taskId,
        rows,
        taskDetails,
        nextStatusCode,
        progressIso,
        user,
        database: 'PPMTRAIN',
      });

      if (result.updatesSent === 0 && !result.taskUpdated) {
        message.info('No changes to save');
        return;
      }

      if (result.failCount === 0) {
        message.success('Progress updated successfully');
      } else if (result.okCount === 0) {
        message.error('Failed to save progress updates');
      } else {
        message.warning(`Saved with warnings: ${result.okCount} updated, ${result.failCount} failed`);
      }

      // Patch react-query cache so activity selector re-select uses fresh status/progress (Option B)
      try {
        queryClient.setQueryData(
          ['primavera_tasks_small', { database: 'PPMTRAIN', v: 'v2' }],
          (old: any) => {
            if (!Array.isArray(old)) return old;
            return old.map((t: any) => {
              if (String(t?.task_id) !== String(taskId)) return t;
              const next: any = { ...t };

              if (nextStatusCode != null) next.status_code = nextStatusCode;

              if (nextStatusCode === 'TK_Active' && !t?.act_start_date) next.act_start_date = progressIso;
              if (nextStatusCode === 'TK_Complete') next.act_end_date = progressIso;
              if (nextStatusCode === 'TK_NotStart') {
                next.act_start_date = null;
                next.act_end_date = null;
              }

              if (t?.complete_pct_type === 'CP_Phys') {
                next.phys_complete_pct = computedPhysCompletePct ?? 0;
              }
              if (result.computedRemainDrtnHrCnt != null) {
                next.remain_drtn_hr_cnt = result.computedRemainDrtnHrCnt;
              }

              return next;
            });
          }
        );
      } catch {
        // ignore
      }

      // React-query hook will refetch automatically when invalidated; for now keep current UI state

      // Keep local taskDetails in sync with what we just saved
      setTaskDetails((prev) => {
        if (!prev) return prev;
        const next: TaskDetails = { ...prev };
        if (nextStatusCode != null) next.status_code = nextStatusCode;
        next.complete_pct_type = prev.complete_pct_type;
        if (prev.complete_pct_type === 'CP_Phys') {
          next.phys_complete_pct = computedPhysCompletePct ?? 0;
          if (result.computedRemainDrtnHrCnt != null) {
            next.remain_drtn_hr_cnt = result.computedRemainDrtnHrCnt;
          }
        }
        if (nextStatusCode === 'TK_Active' && !prev.act_start_date) next.act_start_date = progressIso;
        if (nextStatusCode === 'TK_Complete') next.act_end_date = progressIso;
        if (nextStatusCode === 'TK_NotStart') {
          next.act_start_date = null;
          next.act_end_date = null;
          if (prev.complete_pct_type === 'CP_Phys') next.phys_complete_pct = 0;
        }
        return next;
      });

      // Keep selectedActivity in sync too (it is used as the source of truth when re-selecting)
      setSelectedActivity((prev) => {
        if (!prev || String(prev.task_id) !== String(taskId)) return prev;
        const next: PrimaveraTaskSmall = { ...prev };
        if (nextStatusCode != null) (next as any).status_code = nextStatusCode;
        if ((prev as any).complete_pct_type === 'CP_Phys') (next as any).phys_complete_pct = computedPhysCompletePct ?? 0;
        if (result.computedRemainDrtnHrCnt != null) (next as any).remain_drtn_hr_cnt = result.computedRemainDrtnHrCnt;
        if (nextStatusCode === 'TK_Active' && !(prev as any).act_start_date) (next as any).act_start_date = progressIso;
        if (nextStatusCode === 'TK_Complete') (next as any).act_end_date = progressIso;
        if (nextStatusCode === 'TK_NotStart') {
          (next as any).act_start_date = null;
          (next as any).act_end_date = null;
        }
        return next;
      });
    } catch (e: any) {
      message.error(e?.message || 'Failed to save progress updates');
    } finally {
      setSaving(false);
    }
  }, [rows, taskId, taskDetails, user, progressDate, selectedStatusCode, computedPhysCompletePct, queryClient]);

  const columns: ColumnType<EditableTaskRsrcRow>[] = useMemo(() => {
    return [
      {
        title: 'Res.ID',
        dataIndex: 'rsrc_id',
        key: 'rsrc_id',
        width: 70,
      },
      {
        title: 'Unit',
        dataIndex: 'unit_name',
        key: 'unit_name',
        width: 90,
        render: (v: any) => (v == null ? '' : String(v)),
        exportValue: (r: EditableTaskRsrcRow) => r.unit_name ?? '',
      } as any,
      {
        title: 'Res.Code',
        dataIndex: 'rsrc_short_name',
        key: 'rsrc_short_name',
        width: 90,
      },
      {
        title: 'Resource',
        dataIndex: 'rsrc_name',
        key: 'rsrc_name',
        width: 160,
      },
      {
        title: 'Type',
        dataIndex: 'rsrc_type',
        key: 'rsrc_type',
        width: 80,
      },
      {
        title: 'Cost/Qty',
        dataIndex: 'cost_per_qty',
        key: 'cost_per_qty',
        width: 50,
        align: 'right',
        render: (v: any) => {
          const n = toNum(v);
          if (n == null) return '';
          return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
        },
        exportValue: (r: EditableTaskRsrcRow) => toNum(r.cost_per_qty) ?? '',
      } as any,
      {
        title: 'Rate Src',
        dataIndex: 'cost_per_qty_source_type',
        key: 'cost_per_qty_source_type',
        width: 90,
        render: (v: any) => (v == null ? '' : String(v)),
        exportValue: (r: EditableTaskRsrcRow) => r.cost_per_qty_source_type ?? '',
      } as any,
      {
        title: 'Target Qty',
        dataIndex: 'target_qty',
        key: 'target_qty',
        width: 100,
        align: 'right',
        render: (v: any) => (toNum(v) ?? 0).toLocaleString(undefined, { maximumFractionDigits: 6 }),
        exportValue: (r: EditableTaskRsrcRow) => toNum(r.target_qty) ?? '',
      } as any,
      {
        title: 'Actual Qty',
        dataIndex: '_new_act_reg_qty',
        key: '_new_act_reg_qty',
        width: 100,
        align: 'right',
        render: (_: any, record: EditableTaskRsrcRow) => {
          const disabled =
            saving ||
            isLoadingTaskRsrc ||
            selectedStatusCode === 'TK_Complete' ||
            selectedStatusCode === 'TK_NotStart';
          return (
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={record._new_act_reg_qty ?? 0}
              onChange={(val) => {
                const n = toNum(val);
                const target = toNum(record.target_qty) ?? 0;
                const nextRemain = round6(clampNonNegative(target - (toNum(n) ?? 0)));
                setRows((prev) =>
                  prev.map((r) =>
                    r.taskrsrc_id === record.taskrsrc_id
                      ? { ...r, _new_act_reg_qty: n, _new_remain_qty: nextRemain }
                      : r
                  )
                );
              }}
              disabled={disabled}
            />
          );
        },
        exportValue: (r: EditableTaskRsrcRow) => toNum(r._new_act_reg_qty) ?? '',
      } as any,
      {
        title: 'Remaining Qty',
        key: 'calc_remain_qty',
        width: 100,
        align: 'right',
        render: (_: any, record: EditableTaskRsrcRow) => {
          const remain = clampNonNegative(toNum(record._new_remain_qty) ?? 0);
          return remain.toLocaleString(undefined, { maximumFractionDigits: 6 });
        },
        exportValue: (r: EditableTaskRsrcRow) => {
          return clampNonNegative(toNum(r._new_remain_qty) ?? 0);
        },
      } as any,
    ];
  }, [saving, isLoadingTaskRsrc, selectedStatusCode]);

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Title level={3} style={{ margin: 0 }}>Schedule Progress Update</Title>
          <Space>
            <DatePicker
              value={progressDate}
              onChange={(d) => setProgressDate(d ?? dayjs())}
              disabled={saving || isLoadingTaskRsrc}
              allowClear={false}
            />
            <Select
              style={{ width: 180 }}
              placeholder="Status"
              value={selectedStatusCode ?? undefined}
              onChange={(v) => {
                const next = v ?? null;

                const currentStatus = taskDetails?.status_code != null ? String(taskDetails.status_code) : null;
                if (next === 'TK_Complete' && currentStatus !== 'TK_Active' && currentStatus !== 'TK_Complete') {
                  message.error('You can only set status to TK_Complete when current status is TK_Active');
                  return;
                }

                setSelectedStatusCode(next);

                if (next === 'TK_NotStart') {
                  setRows((prev) =>
                    prev.map((r) => {
                      const target = toNum(r.target_qty) ?? 0;
                      const remain = round6(clampNonNegative(target));
                      return {
                        ...r,
                        _new_act_reg_qty: 0,
                        _new_remain_qty: remain,
                      };
                    })
                  );
                }

                if (next === 'TK_Complete') {
                  setRows((prev) =>
                    prev.map((r) => ({
                      ...r,
                      _new_remain_qty: 0,
                    }))
                  );
                }

                if (next === 'TK_Active') {
                  setRows((prev) =>
                    prev.map((r) => ({
                      ...r,
                      _new_act_reg_qty: toNum(r.act_reg_qty),
                      _new_remain_qty: toNum(r.remain_qty),
                    }))
                  );
                }
              }}
              disabled={!selectedActivity || saving || isLoadingTaskRsrc}
              options={statusOptions}
              showSearch
              filterOption={(input, option) => String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
            />
            {isPhysPct && (
              <InputNumber
                style={{ width: 140 }}
                min={0}
                max={100}
                value={computedPhysCompletePct ?? 0}
                disabled
                addonAfter="%"
              />
            )}
            <Button onClick={() => setActivityModalOpen(true)}>
              Select Activity
            </Button>
            <Button
              action="edit"
              type="primary"
              onClick={handleSave}
              disabled={!selectedActivity || !rows.length || (!hasChanges && !statusChanged) || saving || isLoadingTaskRsrc}
              loading={saving}
            >
              Save
            </Button>
          </Space>
        </div>

        <CombinedActivitySelectorModal
          open={activityModalOpen}
          value={selectedActivity ? String(selectedActivity.task_id) : undefined}
          onOk={(act) => {
            setSelectedActivity(act);
            setActivityModalOpen(false);
          }}
          onCancel={() => setActivityModalOpen(false)}
        />

        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Task Code">{selectedActivity?.task_code ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Task Name">{selectedActivity?.task_name ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Total Cost">
            {Number.isFinite(computedTotalCost)
              ? computedTotalCost.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Actual Cost">
            {Number.isFinite(computedActualCost)
              ? computedActualCost.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Actual Start">{formatDate(taskDetails?.act_start_date)}</Descriptions.Item>
          <Descriptions.Item label="Actual End">{formatDate(taskDetails?.act_end_date)}</Descriptions.Item>
          <Descriptions.Item label="Target Start / Finish">
            {`${formatDate(taskDetails?.target_start_date)} / ${formatDate(taskDetails?.target_end_date)}`}
          </Descriptions.Item>
        </Descriptions>

        <Divider style={{ margin: '8px 0' }} />

        {!selectedActivity ? (
          <Text type="secondary">Select an activity to load its resources.</Text>
        ) : isLoadingTaskRsrc ? (
          <Spin />
        ) : (
          <StandardTable
            dataSource={rows}
            columns={columns as any}
            rowKey={(r) => String(r.taskrsrc_id)}
            bordered
            enableExport
            pagination={{ pageSize: 20, showSizeChanger: true }}
          />
        )}
      </Space>
    </Card>
  );
};

export default ToPlnSchProgressUpdate;
